/**
 * SelfAgentPage — «Наша Тоня»: внутренняя политика агента ТАЧБРЕНД.
 *
 * До 07.08 промпт нашей Тони был зашит в код и правился только релизом, тогда
 * как у каждого клиента персона редактируется в карточке. Эта страница уравнивает
 * их: та же инструкция и та же база знаний, только для себя.
 *
 * Отличие от клиентского редактора: у нас нет СДЕЛКИ, поэтому правки идут не в
 * knowledge_topics, а прямо в реестр Тони (`tenant_prompts`) через admin-ручку.
 * Ворот и одобрения здесь нет — свою политику мы меняем сразу.
 */

import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Bot, History, Loader2, RotateCcw, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { selfAgentApi } from '@/api/client'
import { AgentKbFiles } from '@/components/AgentKbFiles'
import { AgentsBar } from '@/components/AgentsBar'
import { KbTopicsEditor, type KbTopic } from '@/components/KbTopicsEditor'
import { SpecialistPanel } from '@/components/SpecialistPanel'

export function SelfAgentPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<'instruction' | 'kb' | 'channels' | 'test'>('instruction')
  // null — правим основного агента; иначе slug выбранного специалиста (08.08).
  const [agent, setAgent] = useState<string | null>(null)
  const [kbPane, setKbPane] = useState<'topics' | 'documents' | 'tables'>('topics')
  const [showHistory, setShowHistory] = useState(false)
  const [restoring, setRestoring] = useState<string | null>(null)
  const [chat, setChat] = useState<{ role: 'user' | 'bot' | 'error'; text: string }[]>([])
  const [chatInput, setChatInput] = useState('')
  const [chatBusy, setChatBusy] = useState(false)
  const [draft, setDraft] = useState('')
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['self-agent'],
    queryFn: () => selfAgentApi.get(),
    staleTime: 30 * 1000,
  })

  const { data: versions } = useQuery({
    queryKey: ['self-versions'],
    queryFn: () => selfAgentApi.versions(),
    staleTime: 30 * 1000,
    enabled: showHistory,
  })

  const { data: delay } = useQuery({
    queryKey: ['self-delay'],
    queryFn: () => selfAgentApi.getDelay(),
    staleTime: 60 * 1000,
    enabled: tab === 'channels',
  })

  const { data: channels } = useQuery({
    queryKey: ['self-channels'],
    queryFn: () => selfAgentApi.channels(),
    staleTime: 60 * 1000,
    enabled: tab === 'channels',
  })

  // Если своего промпта ещё нет — подставляем ДЕЙСТВУЮЩИЙ (из кода), чтобы
  // правка начиналась с реального текста, а не с пустого поля.
  const stored = data?.system_prompt ?? ''
  const effective = data?.effective_system_prompt ?? ''
  const serverPrompt = stored || effective
  const usingCodePrompt = !stored && !!effective
  const topics: KbTopic[] = data?.kb_topics ?? []

  useEffect(() => {
    if (!dirty) setDraft(serverPrompt)
  }, [serverPrompt, dirty])

  async function savePrompt() {
    setSaving(true); setError(null)
    try {
      await selfAgentApi.save({ system_prompt: draft })
      await qc.invalidateQueries({ queryKey: ['self-agent'] })
      setDirty(false); setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
    } finally { setSaving(false) }
  }

  async function persist(next: KbTopic[]) {
    setSaving(true); setError(null)
    try {
      await selfAgentApi.save({ kb_topics: next })
      await qc.invalidateQueries({ queryKey: ['self-agent'] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
    } finally { setSaving(false) }
  }

  async function sendChat(text: string) {
    const msg = text.trim()
    if (!msg || chatBusy) return
    setChatInput('')
    const history = chat.filter((m) => m.role !== 'error')
      .map((m) => ({ role: m.role === 'user' ? ('user' as const) : ('assistant' as const), content: m.text }))
    setChat((c) => [...c, { role: 'user', text: msg }])
    setChatBusy(true)
    try {
      const res = await selfAgentApi.ask(msg, history)
      setChat((c) => [...c, { role: 'bot', text: res.answer || '(пустой ответ)' }])
    } catch (e) {
      setChat((c) => [...c, { role: 'error', text: e instanceof Error ? e.message : 'Ошибка' }])
    } finally { setChatBusy(false) }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold">Наша Тоня</h1>
        <p className="text-sm text-muted-foreground">
          Внутренняя политика агента ТАЧБРЕНД — то, чем он отвечает нашим лидам.
        </p>
      </div>

      {data?.tenant_id && (
        <AgentsBar
          parent={data.tenant_id}
          selected={agent}
          onSelect={setAgent}
          mainLabel="Тоня"
        />
      )}

      {agent ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Специалист</CardTitle>
          </CardHeader>
          <CardContent>
            <SpecialistPanel slug={agent} />
          </CardContent>
        </Card>
      ) : (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="h-4 w-4 text-muted-foreground" />
              Агент ТАЧБРЕНД
            </CardTitle>
            <div className="flex gap-1 rounded-md border p-0.5">
              <button
                type="button"
                onClick={() => setTab('instruction')}
                className={`rounded px-3 py-1 text-xs ${tab === 'instruction' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
              >
                Инструкция
              </button>
              <button
                type="button"
                onClick={() => setTab('kb')}
                className={`rounded px-3 py-1 text-xs ${tab === 'kb' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
              >
                База знаний
              </button>
              <button
                type="button"
                onClick={() => setTab('channels')}
                className={`rounded px-3 py-1 text-xs ${tab === 'channels' ? 'bg-accent font-medium' : 'text-muted-foreground'}`}
              >
                Каналы
              </button>
              <button
                type="button"
                onClick={() => setTab('test')}
                className={`flex items-center gap-1 rounded px-3 py-1 text-xs font-medium ${tab === 'test' ? 'bg-slate-900 text-white' : 'bg-slate-900/5 text-slate-900'}`}
              >
                Тестировать
              </button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Загружаю…
            </div>
          ) : isError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Не удалось прочитать настройки у Тони.
            </div>
          ) : tab === 'instruction' ? (
            <>
              <textarea
                className="min-h-[360px] w-full rounded-md border bg-background px-3 py-2 font-mono text-sm leading-relaxed"
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setDirty(true) }}
                placeholder="Инструкция агента…"
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {draft.length.toLocaleString('ru-RU')} знаков
                  {dirty && ' · есть несохранённые правки'}
                  {usingCodePrompt && !dirty && ' · загружен действующий промпт'}
                </span>
                <div className="flex items-center gap-2">
                  {saved && <span className="text-xs text-green-700">Сохранено</span>}
                  <Button size="sm" className="gap-2" disabled={saving || !dirty} onClick={savePrompt}>
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Сохранить
                  </Button>
                </div>
              </div>
              <div className="rounded-md border">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
                  onClick={() => setShowHistory((v) => !v)}
                >
                  <History className="h-3.5 w-3.5 text-muted-foreground" />
                  История правок
                  <span className="ml-auto text-muted-foreground">
                    {showHistory ? 'скрыть' : 'показать'}
                  </span>
                </button>
                {showHistory && (
                  <div className="divide-y border-t">
                    {!versions ? (
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Загружаю…
                      </div>
                    ) : versions.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-muted-foreground">
                        Правок ещё не было — история появится после первого сохранения.
                      </p>
                    ) : versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-3 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs">
                            {new Date(v.created_at).toLocaleString('ru-RU')}
                            {v.author && <span className="text-muted-foreground"> · {v.author}</span>}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground">
                            инструкция {v.prompt_len.toLocaleString('ru-RU')} знаков · разделов {v.topics}
                            {v.preview && ` · ${v.preview.slice(0, 50)}…`}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 text-xs"
                          disabled={restoring !== null}
                          onClick={async () => {
                            setRestoring(v.id); setError(null)
                            try {
                              await selfAgentApi.restore(v.id)
                              setDirty(false)
                              await qc.invalidateQueries({ queryKey: ['self-agent'] })
                              await qc.invalidateQueries({ queryKey: ['self-versions'] })
                            } catch (e) {
                              setError(e instanceof Error ? e.message : 'Не удалось вернуть версию.')
                            } finally { setRestoring(null) }
                          }}
                        >
                          {restoring === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          Вернуть
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {usingCodePrompt
                    ? 'В поле — инструкция, по которой Тоня отвечает сейчас (она пока хранится в коде). Правьте и сохраняйте: с этого момента она будет браться из кабинета.'
                    : 'Инструкция хранится в кабинете и применяется сразу — ворот и одобрения здесь нет, это наша собственная политика.'}
                </span>
              </div>
            </>
          ) : tab === 'channels' ? (
            <div className="space-y-3">
              <div className="space-y-2 rounded-lg border p-3">
                <span className="text-sm font-medium">Пауза перед ответом</span>
                <p className="text-xs text-muted-foreground">
                  Тоня ждёт указанное время и отвечает один раз на всё, что лид успел
                  написать. Без паузы — ответ на каждое сообщение отдельно.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[null, 5, 10, 15, 20, 25, 30, 35, 40].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      disabled={saving}
                      onClick={async () => {
                        setSaving(true); setError(null)
                        try {
                          await selfAgentApi.setDelay(v)
                          await qc.invalidateQueries({ queryKey: ['self-delay'] })
                        } catch (e) {
                          setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
                        } finally { setSaving(false) }
                      }}
                      className={`rounded border px-2.5 py-1 text-xs ${
                        (delay?.reply_delay_sec ?? null) === v ? 'bg-accent font-medium' : 'text-muted-foreground'
                      }`}
                    >
                      {v === null ? 'Без паузы' : `${v} сек`}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Проверяется живьём: вебхук Telegram и подписки MAX опрашиваются при
                открытии вкладки. Наличие токена ещё не значит, что сообщения доходят.
              </p>
              {!channels ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Проверяю каналы…
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {channels.map((c) => (
                    <div key={c.key} className={`space-y-1 rounded-lg border p-3 ${c.connected ? 'border-green-300 bg-green-50' : 'bg-card'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{c.name}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] ${c.connected ? 'bg-green-600 text-white' : 'border text-muted-foreground'}`}>
                          {c.connected ? 'Подключён' : 'Не подключён'}
                        </span>
                      </div>
                      <p className={`text-[11px] ${c.connected ? 'text-green-800' : 'text-slate-500'}`}>{c.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : tab === 'test' ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Тот же движок и та же база знаний, что у живой Тони. Диалоги лидов
                и передачи не затрагиваются.
              </p>
              <div className="h-[320px] space-y-2 overflow-y-auto rounded-md border bg-slate-50/60 p-3">
                {chat.length === 0 ? (
                  <p className="pt-24 text-center text-sm text-muted-foreground">
                    Напишите как лид — Тоня ответит по текущей политике.
                  </p>
                ) : chat.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-slate-900 text-white' : m.role === 'error' ? 'border border-amber-200 bg-amber-50 text-amber-900' : 'border bg-background'}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {chatBusy && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Тоня печатает…
                  </div>
                )}
              </div>
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); sendChat(chatInput) }}>
                <input
                  className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="Сообщение…"
                  value={chatInput}
                  disabled={chatBusy}
                  onChange={(e) => setChatInput(e.target.value)}
                />
                <Button type="submit" size="sm" disabled={chatBusy || !chatInput.trim()}>Отправить</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setChat([])}>Очистить</Button>
              </form>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-0.5 rounded border p-0.5 text-xs w-fit">
                {([['topics','Разделы'],['documents','Документы'],['tables','Таблицы']] as const).map(([k,l]) => (
                  <button key={k} type="button" onClick={() => setKbPane(k)}
                          className={`rounded px-2 py-0.5 ${kbPane === k ? 'bg-accent font-medium' : 'text-muted-foreground'}`}>
                    {l}
                  </button>
                ))}
              </div>

              {kbPane !== 'topics' ? (
                <AgentKbFiles slug={data?.tenant_id ?? ''} kind={kbPane === 'tables' ? 'table' : 'document'} />
              ) : (
                <KbTopicsEditor topics={topics} onPersist={persist} saving={saving} />
              )}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {error}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  )
}
